import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  completeConnectionOAuth,
  createConnection,
  deleteConnection,
  getConnectedConnectors,
  getConnection,
  getConnectionActiveWorkflow,
  getConnectionHealth,
  getConnectionsHealth,
  getConnectionUploadTargets,
  getGoogleAdsCustomers,
  getSupplementalDataSources,
  getUploadTargets,
  listConnections,
  listPipelines,
  pauseConnection,
  resumeConnection,
  startConnectionOAuth,
  updateConnection,
  validateConnection,
  type UpdateConnectionRequest,
} from '../services/connection.service';
import type {
  ConnectionResponse,
  ConnectionStatus,
  ConnectionType,
  CreateConnectionRequest,
} from '../types/connection.types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useConnectedConnectors(subsidiaryId?: string) {
  return useQuery({
    queryKey: queryKeys.connections.connected(subsidiaryId),
    queryFn: () => getConnectedConnectors(subsidiaryId),
  });
}

/** Aggregate health across every connection in the subsidiary. The Connect
 *  Platforms screen fires this once on load to badge each connected card as
 *  healthy/unhealthy. On failure the caller treats missing data as "unknown"
 *  and renders no badge, so the page degrades to its pre-health appearance. */
export function useConnectionsHealth(subsidiaryId?: string) {
  return useQuery({
    queryKey: queryKeys.connections.health(subsidiaryId),
    queryFn: () => getConnectionsHealth(subsidiaryId),
    // The aggregate check validates every connector's credentials server-side,
    // so it's relatively expensive — don't silently re-run it on every window
    // refocus. Mutations still invalidate it via the `connections.all` root.
    refetchOnWindowFocus: false,
  });
}

/** Per-connection health detail (component breakdown). Fetched lazily — the
 *  caller passes `enabled: true` only when the user hovers an unhealthy
 *  connection's heart, so we don't run a live check for every connection on
 *  load. `staleTime` keeps a hovered result warm across quick re-hovers. */
export function useConnectionHealth(
  id: string | undefined,
  enabled: boolean,
  subsidiaryId?: string,
) {
  return useQuery({
    queryKey: id
      ? queryKeys.connections.healthById(id, subsidiaryId)
      : queryKeys.disabledDetail.connection(),
    queryFn: () => {
      if (!id) throw new Error('useConnectionHealth: id required when enabled');
      return getConnectionHealth(id, subsidiaryId);
    },
    enabled: enabled && !!id,
    staleTime: 30_000,
  });
}

/** Full-record connections listing, optionally filtered by status. The Connect
 *  Platforms screen passes `['ACTIVE', 'PAUSED']` so paused connections surface
 *  alongside active ones (with a resume affordance) — the connector-summary
 *  `useConnectedConnectors` endpoint doesn't expose status. The status filter
 *  is part of the query key so differently-filtered lists keep separate cache
 *  entries. */
export function useListConnections(
  statuses?: ConnectionStatus[],
  subsidiaryId?: string,
  category?: string,
  /** False keeps it idle — e.g. a sheet that stays mounted while closed. */
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.connections.list(statuses, subsidiaryId, category),
    queryFn: () => listConnections(statuses, subsidiaryId, category),
    enabled,
    // Connections change rarely mid-session; don't refetch on every mount/focus.
    staleTime: 60 * 1000,
  });
}

/** File-upload destinations for a connector, addressed by merchant id
 *  (pre-connection). Backs the onboarding SFTP target picker: enabled only once
 *  a connector code + merchant id are known. 4xx are terminal (bad merchant id,
 *  connector doesn't support uploads, or advance-mode env missing) so we don't
 *  retry them — the picker just renders disabled. */
export function useUploadTargets(connectorCode: string, merchantId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.connectors.uploadTargets(connectorCode, merchantId),
    queryFn: () => getUploadTargets(connectorCode, merchantId),
    enabled: enabled && !!connectorCode && !!merchantId,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = error instanceof ApiError ? error.statusCode : undefined;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });
}

/** File-upload destinations for an existing connection, resolved by its own
 *  handler from `connection.config`. Use this over `useUploadTargets` whenever a
 *  connection exists: that one addresses the connector by a bare merchant id, so
 *  it only fits GMC, while this fits any connector that opts into upload
 *  delivery. Same terminal-4xx policy — a connector without the capability 400s,
 *  and a connection missing its addressing config (Meta's `catalog_id`) won't
 *  start resolving on a retry. */
export function useConnectionUploadTargets(
  connectionId: string,
  enabled = true,
  subsidiaryId?: string,
) {
  return useQuery({
    queryKey: queryKeys.connections.uploadTargets(connectionId, subsidiaryId),
    queryFn: () => getConnectionUploadTargets(connectionId, subsidiaryId),
    enabled: enabled && !!connectionId,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = error instanceof ApiError ? error.statusCode : undefined;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });
}

/** GMC supplemental data sources for the per-locale destination picker. Keyed by
 *  connection in edit mode and by merchant id on create; same terminal-4xx retry
 *  policy as the upload-target hooks (a wrong merchant id won't fix itself). */
export function useSupplementalDataSources(
  { connectionId, merchantId }: { connectionId?: string | undefined; merchantId: string },
  enabled = true,
) {
  return useQuery({
    queryKey: connectionId
      ? queryKeys.connections.supplementalDataSources(connectionId)
      : queryKeys.connectors.supplementalDataSources('GOOGLE_MERCHANT', merchantId),
    queryFn: () =>
      getSupplementalDataSources(
        connectionId ? { connectionId } : { connectorCode: 'GOOGLE_MERCHANT', merchantId },
      ),
    enabled: enabled && (!!connectionId || !!merchantId),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = error instanceof ApiError ? error.statusCode : undefined;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });
}

/** Resume-dispatch check for a GOOGLE_ADS connect-mode row sitting at
 *  `PENDING`/`ERROR` — a plain `GET` can't tell "hasn't signed in yet" apart
 *  from "signed in, needs an account pick" (both are `PENDING`), so callers
 *  enable this only when they've already confirmed `config.connect` and a
 *  resumable status. Read via `readGoogleAdsOAuthDetails` in
 *  `google-ads-oauth.ts`. `staleTime: 0` — this is a navigation gate read
 *  fresh on each resume, not display data worth caching. */
export function useGoogleAdsOAuthValidation(
  connectionId: string | undefined,
  enabled: boolean,
  subsidiaryId?: string,
) {
  return useQuery({
    queryKey: connectionId
      ? queryKeys.connections.oauthValidation(connectionId, subsidiaryId)
      : queryKeys.disabledDetail.connection(),
    queryFn: () => {
      if (!connectionId) {
        throw new Error('useGoogleAdsOAuthValidation: connectionId required when enabled');
      }
      return validateConnection(connectionId, subsidiaryId);
    },
    enabled: enabled && !!connectionId,
    staleTime: 0,
  });
}

/** Connect-mode account picker — the Google Ads accounts the signed-in user
 *  can access. Enabled only once the caller knows step 4 (oauth/callback) has
 *  completed (the connection is `PENDING` with `details.pending_customer_
 *  selection`), same enabled-gating convention as `useConnectionUploadTargets`. */
export function useGoogleAdsCustomers(
  connectionId: string | undefined,
  enabled: boolean,
  subsidiaryId?: string,
) {
  return useQuery({
    queryKey: connectionId
      ? queryKeys.connections.googleAdsCustomers(connectionId, subsidiaryId)
      : queryKeys.disabledDetail.connection(),
    queryFn: () => {
      if (!connectionId)
        throw new Error('useGoogleAdsCustomers: connectionId required when enabled');
      return getGoogleAdsCustomers(connectionId, subsidiaryId);
    },
    enabled: enabled && !!connectionId,
  });
}

/** Hard cap on `useConnection` polling — the wrapper stops scheduling
 *  refetches once this many total attempts have landed (successes + errors
 *  combined), regardless of the caller's predicate. At the typical 3s
 *  interval that's ~1 minute of polling. Exported so callers (and the unit
 *  test below) can reason about and assert the bound.
 *
 *  Recovery once capped: refetchInterval returns `false`, so background
 *  polling stops. Window-focus / mount / manual `queryClient.invalidate`
 *  still triggers a fresh fetch — the cap is a runaway-polling defence,
 *  NOT a "stop forever" signal. If the backend enriches after the cap
 *  fires, a window-focus refetch picks it up.
 *
 *  Why count errors too: `Query.state.dataUpdateCount` only increments on
 *  success; `errorUpdateCount` on failure. A backend that alternates
 *  success/error would otherwise poll well past the documented "60s" budget
 *  before the success counter alone hit the cap. */
export const USE_CONNECTION_POLL_MAX_ATTEMPTS = 20;

/** Inputs to the polling-cap wrapper. Matches the shape of the
 *  `Query` object TanStack v5 passes to `refetchInterval`, but typed as a
 *  loose subset so we can unit-test the wrapper without a full TanStack
 *  Query mock. */
interface PollingQueryState {
  state: {
    error: Error | null;
    data: ConnectionResponse | undefined;
    dataUpdateCount: number;
    errorUpdateCount: number;
  };
}

/**
 * Wrap a caller-supplied `refetchInterval` predicate with the hook-level
 * safety rails:
 *   1. Stop polling on any query error (cached data is stale; caller's
 *      predicate can't see the new failure).
 *   2. Cap total attempts (successes + errors) at
 *      USE_CONNECTION_POLL_MAX_ATTEMPTS. Counting BOTH counters matters:
 *      `dataUpdateCount` alone is success-only in TanStack v5, so an
 *      interleaved failure stream would otherwise extend polling past
 *      the documented budget.
 *   3. Delegate to the caller's predicate below the rails.
 *
 * Exported for unit tests so the behavior is pinned independently of any
 * `renderHook` plumbing — the property "polling is bounded under any
 * success/error mix" is what the rails enforce.
 */
export function makePollingPredicate(
  callerPredicate: (data: ConnectionResponse | undefined) => number | false,
): (query: PollingQueryState) => number | false {
  return (query) => {
    if (query.state.error) return false;
    const totalAttempts = query.state.dataUpdateCount + query.state.errorUpdateCount;
    if (totalAttempts >= USE_CONNECTION_POLL_MAX_ATTEMPTS) return false;
    return callerPredicate(query.state.data);
  };
}

/** Fetch a single connection (full record, including `config`).
 *
 *  - Enabled only when an id is provided.
 *  - `id === undefined` keys off `queryKeys.disabledDetail.connection()` which
 *    lives OUTSIDE the `['connections']` tree, so `invalidateQueries({queryKey:
 *    queryKeys.connections.all})` from mutations doesn't refetch a disabled
 *    query that has no queryFn resolution path.
 *  - `refetchInterval` lets callers poll while a derived condition is
 *    unsatisfied (e.g. Meta hub polls until `config.catalog_account` enriches
 *    on the backend). Called with the current `data`; return `false` to stop.
 *
 *  Hook-level safety rails on the polling: stop on any query error (a 4xx/5xx
 *  shouldn't keep polling — `data` might still be present from a prior
 *  success, so the caller's predicate would otherwise be blind to the new
 *  failure), and cap at `USE_CONNECTION_POLL_MAX_ATTEMPTS` total fetches. */
export function useConnection(
  id: string | undefined,
  refetchInterval?: (data: ConnectionResponse | undefined) => number | false,
  subsidiaryId?: string,
) {
  return useQuery<ConnectionResponse>({
    queryKey: id
      ? queryKeys.connections.byId(id, subsidiaryId)
      : queryKeys.disabledDetail.connection(),
    queryFn: () => {
      // Disabled when `id` is undefined; this throw documents the invariant
      // and keeps `getConnection` strictly typed as `string`-only.
      if (!id) throw new Error('useConnection: id required when enabled');
      return getConnection(id, subsidiaryId);
    },
    enabled: !!id,
    refetchInterval: refetchInterval ? makePollingPredicate(refetchInterval) : false,
  });
}

/** One-shot lookup of a connection's active pipeline workflow, used by the
 *  onboarding hub to resume an in-flight catalog import. Enabled only once an
 *  `id` is known; `retry: false` because the backend 404 ("nothing running")
 *  is already mapped to `null` in the service, so any thrown error is a real
 *  failure not worth re-attempting before the user acts.
 *
 *  `staleTime`/`gcTime: 0` deliberately override the 5-minute global default:
 *  this is a navigation gate, not display data. Without it, a `null` cached on
 *  the user's first hub visit would still be "fresh" when they return after
 *  starting an import — so the resume redirect would never fire, defeating the
 *  fix on the exact round-trip it targets (BUG-070). Re-asking every mount keeps
 *  the decision in step with the live backend state. */
export function useConnectionActiveWorkflow(id: string | undefined, connectorCode: ConnectionType) {
  return useQuery({
    queryKey: id
      ? queryKeys.connections.activeWorkflow(id, connectorCode)
      : queryKeys.disabledDetail.connection(),
    queryFn: () => {
      if (!id) throw new Error('useConnectionActiveWorkflow: id required when enabled');
      return getConnectionActiveWorkflow(id, connectorCode);
    },
    enabled: !!id,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create-or-reactivate a connection. The hook owns the top-level toast;
 *  field-level validation errors are applied per-form via
 *  `applyApiFieldErrors`. Any field path the screen's `*_FIELD_ERROR_MAP`
 *  doesn't claim surfaces as a top-level toast via `extractApiError` below.
 *
 *  Reactivation flow: before creating, the hook lists existing connections
 *  and looks for a row of the same `type` in a non-ACTIVE state (typically
 *  PAUSED — the disconnect-error fallback leaves rows in that state when a
 *  pipeline is still bound to them). When found, the payload is applied via
 *  PATCH on the existing id instead of POSTing a duplicate. ACTIVE rows are
 *  ignored here so the existing 409-on-create flow still triggers each
 *  screen's "already connected" recovery branch.
 *
 *  409 Conflict is explicitly NOT toasted here — the backend's duplicate
 *  error message ("Connection with name '…' already exists") would only
 *  confuse users during a retry, and callers already implement recovery by
 *  looking up the existing connection via `getConnectedConnectors`. */
export function useCreateConnection(subsidiaryId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('onboarding');

  return useMutation({
    mutationFn: async (data: CreateConnectionRequest) => {
      // Look up an existing non-ACTIVE row of the same type so a previously
      // paused/disabled connection is reactivated instead of duplicated.
      // The list call is best-effort — any failure (network, 5xx, auth)
      // silently falls through to POST so the user isn't blocked.
      let reactivateId: string | null = null;
      try {
        const existing = await listConnections(undefined, subsidiaryId);
        // A GOOGLE_ADS row at PENDING always means "waiting on the
        // interactive OAuth flow" (see google-ads-oauth-connect-ui-integration
        // .md) — PATCHing it to ACTIVE 400s ("cannot be activated yet"). Excluded
        // here so a genuine duplicate-create attempt falls through to POST →
        // 409, whose callers route into the resume panel instead of retrying
        // this reactivate-via-PATCH path.
        const match = existing.find(
          (c) =>
            c.type === data.type &&
            c.status !== 'ACTIVE' &&
            !(c.type === 'GOOGLE_ADS' && c.status === 'PENDING'),
        );
        if (match) reactivateId = match.id;
      } catch {
        // Listing failed — drop to POST.
      }
      if (reactivateId) {
        // PATCH body mirrors the POST body (minus `type` — the URL keys the
        // row) plus `status: ACTIVE` to flip the row out of its non-ACTIVE
        // state. `delivery_method` is hoisted from `config` to the top level
        // because backends like GMC store it on both — keeping the two in
        // sync on the wire matches what the connection record actually
        // looks like server-side.
        //
        // `credentials` is always sent. For GMC the API path supplies a
        // `boost` marker with no `SFTP` block; backend expects the SFTP key
        // to be present-and-null (not absent) so the wire shape is fully
        // determined — we normalise that here. Connectors whose credentials
        // don't carry the `boost` marker (Shopify / GA4 / Ads / Meta) pass
        // through unchanged.
        const deliveryMethod =
          typeof data.config.delivery_method === 'string' ? data.config.delivery_method : undefined;
        const credentialsForPatch =
          data.credentials && 'boost' in data.credentials && !('SFTP' in data.credentials)
            ? { ...data.credentials, SFTP: null }
            : data.credentials;
        return updateConnection(
          reactivateId,
          {
            name: data.name,
            config: data.config,
            ...(credentialsForPatch ? { credentials: credentialsForPatch } : {}),
            status: 'ACTIVE',
            ...(deliveryMethod ? { delivery_method: deliveryMethod } : {}),
          },
          subsidiaryId,
        );
      }
      return createConnection(data, subsidiaryId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
      toast.success(t('connectionStatus.toasts.created'));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.statusCode === 409) return;
      toast.error(extractApiError(err, t('connectionStatus.toasts.createError')));
    },
  });
}

/** Pull platform account info for an existing connection. Populates the
 *  confirmation/preview screens with account-level context (campaigns,
 *  users, report suites, etc.). */
export function useValidateConnection(subsidiaryId?: string) {
  const { t } = useTranslation('onboarding');
  return useMutation({
    mutationFn: (connectionId: string) => validateConnection(connectionId, subsidiaryId),
    onError: (err) => {
      toast.error(extractApiError(err, t('connectionStatus.toasts.validateError')));
    },
  });
}

/** Start the Google Ads interactive OAuth flow — mints the Google
 *  consent-screen URL for a `PENDING` connection. Callers redirect
 *  `window.location.href` to `data.oauth_url` on success; no toast here
 *  since a redirect follows immediately and there's nothing left to read. */
export function useStartConnectionOAuth(subsidiaryId?: string) {
  const { t } = useTranslation('onboarding');
  return useMutation({
    mutationFn: ({ connectionId, redirectUri }: { connectionId: string; redirectUri: string }) =>
      startConnectionOAuth(connectionId, redirectUri, subsidiaryId),
    onError: (err) => {
      toast.error(extractApiError(err, t('connectionStatus.toasts.oauthStartError')));
    },
  });
}

/** Complete the Google Ads interactive OAuth flow — exchanges the
 *  Google-redirect `code` for tokens. Invalidates the connections root on
 *  success so the hub/list pick up the new `ACTIVE` status immediately. No
 *  toast on error: the callback page surfaces the `detail` message itself
 *  alongside its retry affordance. */
export function useCompleteConnectionOAuth(subsidiaryId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectionId,
      code,
      redirectUri,
    }: {
      connectionId: string;
      code: string;
      redirectUri: string;
    }) => completeConnectionOAuth(connectionId, code, redirectUri, subsidiaryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
    },
  });
}

/** Variables for `useDeleteConnection`. `connectorCode` is required so the
 *  INTEGRITY_ERROR fallback below can correlate the failed connection with
 *  any active pipeline rows returned by `listPipelines()` — pipelines are
 *  keyed by connector code, not connection id. */
export interface DeleteConnectionVariables {
  connectionId: string;
  connectorCode: ConnectionType;
}

/** Delete a connection. Invalidates the connected list so the Connect
 *  Platforms cards re-render as "not connected" immediately. Caller handles
 *  per-platform store cleanup (e.g. clearing googleAnalyticsValidation).
 *
 *  Error handling:
 *  - On `INTEGRITY_ERROR` (server still has a pipeline bound to this
 *    connector) the hook fetches `pipelines/list`, matches by
 *    `connectorCode`, and — if a match exists — surfaces a recovery toast
 *    offering to pause the connection instead. The pause runs inline (no
 *    separate hook) since it's only reachable from this failure path; on
 *    success it invalidates the connected list so the card flips to a
 *    paused state.
 *  - Any other error (or a pipelines-list lookup that fails / returns no
 *    matching row) falls through to the generic remove-error toast.
 */
export function useDeleteConnection(subsidiaryId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('onboarding');

  return useMutation({
    mutationFn: ({ connectionId }: DeleteConnectionVariables) =>
      deleteConnection(connectionId, subsidiaryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
      toast.success(t('connectionStatus.toasts.removed'));
    },
    onError: async (err, variables) => {
      if (err instanceof ApiError && err.serverCode === 'INTEGRITY_ERROR') {
        try {
          const pipelines = await listPipelines();
          const blocking = pipelines.some((p) => p.connectorCode === variables.connectorCode);
          if (blocking) {
            toast.error(t('connectionStatus.toasts.removeBlockedByPipeline'), {
              action: {
                label: t('connectionStatus.toasts.pauseAction'),
                onClick: () => {
                  void pauseConnection(variables.connectionId, subsidiaryId)
                    .then(() => {
                      toast.success(t('connectionStatus.toasts.paused'));
                      void queryClient.invalidateQueries({
                        queryKey: queryKeys.connections.all,
                      });
                    })
                    .catch((pauseErr: unknown) => {
                      toast.error(
                        extractApiError(pauseErr, t('connectionStatus.toasts.pauseError')),
                      );
                    });
                },
              },
            });
            return;
          }
        } catch {
          // Pipelines lookup failed — drop to the generic toast so the
          // user still gets feedback rather than silent swallow.
        }
      }
      toast.error(extractApiError(err, t('connectionStatus.toasts.removeError')));
    },
  });
}

/** Pause an active connection. Invalidates the connections root so every list
 *  and health query re-reads and the row flips to its paused (resume)
 *  affordance immediately. Standalone counterpart to the inline pause used by
 *  `useDeleteConnection`'s INTEGRITY_ERROR recovery — surfaced on the
 *  Connections management page's row actions. */
export function usePauseConnection(subsidiaryId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('onboarding');

  return useMutation({
    mutationFn: (connectionId: string) => pauseConnection(connectionId, subsidiaryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
      toast.success(t('connectionStatus.toasts.paused'));
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('connectionStatus.toasts.pauseError')));
    },
  });
}

/** Resume a paused connection. Invalidates the connected list so the Connect
 *  Platforms cards flip from the paused (resume) affordance back to the active
 *  (connected) state immediately. */
export function useResumeConnection(subsidiaryId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('onboarding');

  return useMutation({
    mutationFn: (connectionId: string) => resumeConnection(connectionId, subsidiaryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
      toast.success(t('connectionStatus.toasts.resumed'));
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('connectionStatus.toasts.resumeError')));
    },
  });
}

/** Partial update of a connection — e.g. persisting the user's picked GA4
 *  property after the wizard's picker step, or the Connect Platforms edit flow
 *  (config/credentials/delivery_method). Invalidates the connected list so
 *  dependent screens re-read. The success toast is left to callers so the
 *  silent GA4-property-persist path stays quiet while the edit screens can
 *  surface their own confirmation. */
export function useUpdateConnection(subsidiaryId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('onboarding');

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateConnectionRequest }) =>
      updateConnection(id, body, subsidiaryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('connectionStatus.toasts.updateError')));
    },
  });
}
